/**
 * Zentrale Formatter für Decimal-Werte aus dem Backend.
 *
 * Backend-Werte kommen als String (Decimal-serialisiert), damit keine
 * Float-Präzision verloren geht. Diese Helpers konvertieren sie für die
 * Anzeige und behandeln `null`/`undefined` einheitlich mit Em-Dash.
 *
 * Konvention:
 * - `formatNumber` / `formatPct` / `formatMoney`: englisches Format
 *   (Punkt als Dezimal-Trenner). Für mono-Font-Darstellungen, Input-
 *   Werte und Order-Tickets, wo Broker den Wert parsen.
 * - `formatNumberDe`: deutsches Locale (Komma als Dezimal-Trenner,
 *   Punkt als Tausender). Für Prosa-Darstellungen, in denen das
 *   gewohnte Format wichtiger ist als maschinelle Lesbarkeit.
 */

const DASH = '—'

/**
 * Projekt-Konvention: Datums-/Zeit-Anzeige immer in Europe/Berlin, unabhängig
 * von der Client-Timezone. Setzt offset-behaftete ISO-Strings vom Backend
 * voraus (Schemas serialisieren via UtcDatetime mit UTC-Offset).
 */
const BERLIN_TZ = 'Europe/Berlin'

/** "13.05., 14:30" — Datum + Uhrzeit in Europe/Berlin. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: BERLIN_TZ,
  })
}

/** "13.05." — Tag/Monat in Europe/Berlin. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    timeZone: BERLIN_TZ,
  })
}

/** "14:30" — Stunde/Minute in Europe/Berlin. */
export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: BERLIN_TZ,
  })
}

type Numeric = string | number | null | undefined

function toFiniteNumber(value: Numeric): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/** "123.45" — Punkt-Dezimal, ohne Tausender-Trenner. */
export function formatNumber(value: Numeric, digits = 2): string {
  const n = toFiniteNumber(value)
  return n === null ? DASH : n.toFixed(digits)
}

/** "15.0 %" — Eingabe ist Bruchteil (0.15). */
export function formatPct(value: Numeric, digits = 1): string {
  const n = toFiniteNumber(value)
  return n === null ? DASH : `${(n * 100).toFixed(digits)} %`
}

/** "123.45 USD" — Punkt-Dezimal, Währung als Suffix. */
export function formatMoney(value: Numeric, currency: string, digits = 2): string {
  const n = toFiniteNumber(value)
  return n === null ? DASH : `${n.toFixed(digits)} ${currency}`
}

/** "1.234,56" — Deutsches Locale mit Tausender-Trenner. */
export function formatNumberDe(value: Numeric, digits = 2): string {
  const n = toFiniteNumber(value)
  return n === null
    ? DASH
    : n.toLocaleString('de-DE', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
}

const EUR_FORMATTER = new Intl.NumberFormat('de-DE', {
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
})

/**
 * "+7.750 €" oder "−4.500 €" — Signierter EUR mit Tausender-Punkt (de-DE).
 *
 * Rundet auf Ganzzahlen, fügt + für positive und U+2212-Minus (−) für
 * negative Werte hinzu. Null bleibt ohne Vorzeichen.
 */
export function formatEurSigned(value: string | number): string {
  const num = typeof value === 'string' ? Number(value) : value
  const rounded = Math.round(num)
  if (rounded === 0) {
    return '0 €'
  }
  const abs = Math.abs(rounded)
  const formatted = EUR_FORMATTER.format(abs)
  const sign = rounded > 0 ? '+' : '−'
  return `${sign}${formatted} €`
}
